import React from "react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <section className="min-h-screen bg-[#0B1C3F] flex items-center justify-center px-4 py-12">
      <div className="relative w-full max-w-7xl bg-white shadow-2xl rounded-3xl border border-slate-200 px-6 py-8 sm:px-10 sm:py-10 md:px-14 md:py-12">
        {/* Header */}
        <header className="mb-8 border-b border-slate-200 pb-4">
          <h1 className="text-3xl md:text-4xl font-semibold text-[#064585]">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Last updated:{" "}
            <span className="font-semibold text-slate-800">
              27 November 2025
            </span>
          </p>
        </header>

        {/* Content */}
        <main className="space-y-8 text-slate-700 text-sm sm:text-base leading-relaxed max-h-[70vh] overflow-y-auto pr-2">
          {/* Intro */}
          <section>
            <p>
              At{" "}
              <span className="font-medium text-[#064585]">
                Web_Bocket Classes
              </span>
              , we respect your privacy and are committed to protecting your
              personal data. This Privacy Policy explains how we collect, use,
              and safeguard your information when you use our website and
              services.
            </p>
          </section>

          {/* 1. Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              1. Information We Collect
            </h2>
            <p className="mb-2">
              We may collect different types of information in order to provide
              and improve our services, including:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-3">
              <li>
                <span className="font-medium text-[#064585]">
                  Personal Information:
                </span>{" "}
                such as your name, email, phone number, and any details you
                provide when creating an account or contacting us.
              </li>
              <li>
                <span className="font-medium text-[#064585]">Usage Data:</span>{" "}
                pages visited, clicks, and time spent on the website.
              </li>
              <li>
                <span className="font-medium text-[#064585]">
                  Technical Data:
                </span>{" "}
                IP address, device type, browser data, and diagnostic info.
              </li>
            </ul>
          </section>

          {/* 2. How We Use Your Information */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              2. How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-3">
              <li>To operate and maintain our website.</li>
              <li>To personalize and enhance your learning experience.</li>
              <li>To respond to your support requests and queries.</li>
              <li>
                To send important notifications, updates, or marketing content
                (where permitted).
              </li>
              <li>To analyze usage trends and improve performance.</li>
            </ul>
          </section>

          {/* 3. Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              3. Cookies &amp; Tracking Technologies
            </h2>
            <p>
              We use cookies to remember your preferences, track website
              performance, and enhance user experience. You may disable cookies
              in your browser, but it may impact your browsing experience.
            </p>
          </section>

          {/* 4. Data Sharing */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              4. How We Share Your Data
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-3">
              <li>
                <span className="font-medium text-[#064585]">
                  Service Providers:
                </span>{" "}
                trusted partners for hosting, analytics, and email.
              </li>
              <li>
                <span className="font-medium text-[#064585]">
                  Legal Compliance:
                </span>{" "}
                when required by law or government authorities.
              </li>
              <li>
                <span className="font-medium text-[#064585]">
                  Business Transfer:
                </span>{" "}
                if Web_Bocket Classes is merged or acquired.
              </li>
            </ul>
          </section>

          {/* 5. Data Security */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              5. Data Security
            </h2>
            <p>
              We use industry-standard precautions to safeguard your data.
              However, no system is fully secure, and we cannot guarantee
              absolute protection.
            </p>
          </section>

          {/* 6. Rights */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              6. Your Rights &amp; Choices
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-3">
              <li>Request access or correction of your data.</li>
              <li>Request deletion of your data (subject to legal limits).</li>
              <li>Opt out of promotional emails.</li>
            </ul>
          </section>

          {/* 7. Links */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              7. Third-Party Links
            </h2>
            <p>
              Our website may include links to third-party websites. We are not
              responsible for their content or privacy practices.
            </p>
          </section>

          {/* 8. Changes */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              8. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this policy from time to time. Updates will be
              posted here with a revised “Last updated” date.
            </p>
          </section>

          {/* 9. Contact */}
          <section>
            <h2 className="text-xl font-semibold text-[#064585] mb-2">
              9. Contact Us
            </h2>
            <p>
              For any privacy-related questions, contact us at:
              <br />
              Email:{" "}
              <a
                href="mailto:classeswebbocket@gmail.com"
                className="text-[#064585] font-medium hover:underline"
              >
                classeswebbocket@gmail.com
              </a>
            </p>
          </section>
        </main>

        {/* Footer */}
        <footer className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-slate-600">
            By using this website, you agree to this Privacy Policy.
          </p>
          <button
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            }}
            type="button"
            className="bg-[#064585] hover:bg-[#033465] text-white text-sm font-medium px-6 py-2 rounded-full shadow-md transition-all duration-200"
          >
            Got It
          </button>
        </footer>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
